import { RiArrowDownSLine, RiCheckLine, RiCloseLine, RiLoader4Line, RiSearchLine } from '@remixicon/react';
import { cva, type VariantProps } from 'class-variance-authority';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Selection } from 'react-aria-components';
import {
  Button,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  SearchField,
  Tag,
  TagGroup,
  TagList,
} from 'react-aria-components';
import { cn } from './utils';

const agMultiAutocompleteTriggerVariants = cva(
  'inline-flex items-center w-full bg-white border transition-all duration-150 cursor-pointer',
  {
    variants: {
      variant: { default: 'border-neutral-5 hover:border-neutral-6', error: 'border-red-text' },
      size: {
        sm: 'min-h-[32px] px-2.5 gap-1.5 text-14',
        md: 'min-h-[40px] px-3 gap-2 text-14',
        lg: 'min-h-[48px] px-1 gap-2.5 text-16',
      },
      shape: { rounded: 'rounded-md', pill: 'rounded-full' },
      isDisabled: { true: 'opacity-60 pointer-events-none bg-neutral-2', false: '' },
    },
    compoundVariants: [
      { variant: 'default', className: 'data-[open]:border-brand-6 data-[open]:ring-2 data-[open]:ring-ring-primary' },
      { variant: 'error', className: 'data-[open]:ring-2 data-[open]:ring-ring-destructive' },
    ],
  }
);

const agLabelVariants = cva('text-neutral-text font-medium', {
  variants: { size: { sm: 'text-13', md: 'text-14', lg: 'text-14' } },
});

type AgMultiAutocompleteVariantProps = VariantProps<typeof agMultiAutocompleteTriggerVariants>;

export interface AgMultiAutocompleteProps<T extends object> {
  label?: string;
  isRequired?: boolean;
  options: T[];
  getOptionLabel: (option: T) => string;
  getOptionKey: (option: T) => string | number;
  placeholder?: string;
  size?: AgMultiAutocompleteVariantProps['size'];
  shape?: AgMultiAutocompleteVariantProps['shape'];
  className?: string;
  isInvalid?: boolean;
  isDisabled?: boolean;
  value?: T[];
  onChange?: (value: T[]) => void;
  limitTags?: number;
  noOptionsText?: ReactNode;
  filterOptions?: boolean;
  onInputChange?: (value: string) => void;
  isLoading?: boolean;
  renderOption?: (option: T, state: { isSelected: boolean; isFocused: boolean }) => ReactNode;
}

export function AgMultiAutocomplete<T extends object>({
  label,
  isRequired,
  options,
  getOptionLabel,
  getOptionKey,
  placeholder = 'Select...',
  size = 'md',
  shape = 'rounded',
  className,
  isInvalid,
  isDisabled,
  value = [],
  onChange,
  limitTags,
  noOptionsText = 'No options found',
  filterOptions = true,
  onInputChange: onInputChangeProp,
  isLoading = false,
  renderOption,
}: AgMultiAutocompleteProps<T>) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const wasOpenOnMouseDown = useRef(false);
  const [searchValue, setSearchValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  // Auto-focus search when opening
  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [isOpen]);

  // Close on outside clicks (isNonModal disables built-in click-outside detection)
  useEffect(() => {
    if (!isOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
      setSearchValue('');
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  const getKey = useCallback((option: T): string => String(getOptionKey(option)), [getOptionKey]);

  // Convert value T[] to selectedKeys Set for ListBox
  const selectedKeys = useMemo(() => {
    return new Set(value.map((item) => getKey(item)));
  }, [value, getKey]);

  // Filter options based on search input (only for local filtering)
  const filteredOptions = useMemo(() => {
    const safeOptions = options ?? [];
    if (!filterOptions || !searchValue) return safeOptions;
    const lower = searchValue.toLowerCase();
    return safeOptions.filter((option) => getOptionLabel(option).toLowerCase().includes(lower));
  }, [options, searchValue, getOptionLabel, filterOptions]);

  // Handle selection change from ListBox
  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      const existingKeys = new Set(value.map(getKey));
      const newItems = options.filter((o) => !existingKeys.has(getKey(o)));
      onChange?.([...value, ...newItems]);
      return;
    }

    const keySet = keys as Set<React.Key>;

    // Keep existing selected items still in the new selection
    const kept = value.filter((item) => keySet.has(getKey(item)));

    // Find newly added items from current options
    const existingKeys = new Set(value.map(getKey));
    const added = options.filter((option) => keySet.has(getKey(option)) && !existingKeys.has(getKey(option)));

    onChange?.([...kept, ...added]);
  };

  // Handle tag removal
  const handleRemove = (keys: Set<React.Key>) => {
    const newValue = value.filter((item) => !keys.has(getKey(item)));
    onChange?.(newValue);
  };

  // Handle search input
  const handleSearchChange = (newValue: string) => {
    setSearchValue(newValue);
    onInputChangeProp?.(newValue);
  };

  // Open dropdown when clicking the trigger area (but not buttons like tag remove)
  const handleTriggerMouseDown = () => {
    wasOpenOnMouseDown.current = isOpen;
  };

  const handleTriggerClick = (e: React.MouseEvent) => {
    if (isDisabled) return;
    if ((e.target as HTMLElement).closest('[slot="remove"]')) return;
    // If popover was open on mousedown, the Popover already closed it — don't reopen
    if (wasOpenOnMouseDown.current) return;
    setIsOpen(true);
  };

  // Tags to display (with limitTags)
  const visibleTags = limitTags != null ? value.slice(0, limitTags) : value;
  const hiddenCount = limitTags != null ? Math.max(0, value.length - limitTags) : 0;

  const tagSize =
    size === 'sm' ? 'px-1.5 py-0.5 text-12' : size === 'lg' ? 'px-2.5 py-1 text-14' : 'px-2 py-0.5 text-13';

  return (
    <div className={cn('relative flex flex-col gap-2 w-full', className)}>
      {label && (
        <Label className={cn(agLabelVariants({ size }))}>
          {label}
          {isRequired && <span className="text-red-text ml-2">*</span>}
        </Label>
      )}
      <div
        ref={triggerRef}
        role="combobox"
        tabIndex={isDisabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        data-open={isOpen || undefined}
        onMouseDown={handleTriggerMouseDown}
        onClick={handleTriggerClick}
        onKeyDown={(e) => {
          if (isDisabled) return;
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
        className={cn(
          agMultiAutocompleteTriggerVariants({ variant: isInvalid ? 'error' : 'default', size, shape, isDisabled }),
          'py-1'
        )}
      >
        <div className="flex-1 flex items-center min-w-0 overflow-hidden">
          {value.length === 0 ? (
            <span className="text-neutral-alpha-7 truncate">{placeholder}</span>
          ) : (
            <div className="flex items-center flex-wrap gap-1">
              <TagGroup aria-label={label ?? 'Selected items'} onRemove={!isDisabled ? handleRemove : undefined}>
                <TagList className="flex flex-wrap gap-1">
                  {visibleTags.map((item) => (
                    <Tag
                      key={getKey(item)}
                      id={getKey(item)}
                      textValue={getOptionLabel(item)}
                      className={cn(
                        'inline-flex items-center gap-2 rounded-full bg-neutral-3 text-neutral-text cursor-default',
                        tagSize
                      )}
                    >
                      {({ allowsRemoving }) => (
                        <>
                          <span className="truncate max-w-[150px]">{getOptionLabel(item)}</span>
                          {allowsRemoving && (
                            <Button
                              slot="remove"
                              className="p-0 rounded-full hover:bg-neutral-5 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring-primary"
                            >
                              <RiCloseLine className="h-3 w-3" aria-hidden="true" />
                            </Button>
                          )}
                        </>
                      )}
                    </Tag>
                  ))}
                </TagList>
              </TagGroup>
              {hiddenCount > 0 && (
                <span className={cn('text-neutral-alpha-8 whitespace-nowrap', tagSize)}>+{hiddenCount} more</span>
              )}
            </div>
          )}
        </div>
        <span className="p-2 text-neutral-alpha-7 shrink-0">
          <RiArrowDownSLine className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-180')} aria-hidden="true" />
        </span>
      </div>
      <Popover
        ref={popoverRef}
        triggerRef={triggerRef}
        isOpen={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) setSearchValue('');
        }}
        placement="bottom start"
        isNonModal
        className={cn(
          'overflow-hidden rounded-md bg-white border border-neutral-5 shadow-lg',
          'entering:animate-in entering:fade-in-0 entering:zoom-in-95',
          'exiting:animate-out exiting:fade-out-0 exiting:zoom-out-95'
        )}
        style={{ width: triggerRef.current?.getBoundingClientRect().width, zIndex: 200000 }}
      >
        <SearchField
          aria-label={`Search ${label ?? 'options'}`}
          value={searchValue}
          onChange={handleSearchChange}
          className="border-b border-neutral-4"
        >
          <div className="flex items-center gap-1.5 px-2 py-1.5">
            <RiSearchLine className="h-3.5 w-3.5 text-neutral-alpha-7 shrink-0" aria-hidden="true" />
            <Input
              ref={searchInputRef}
              placeholder="Search..."
              className="flex-1 bg-transparent outline-none text-14 text-neutral-text placeholder:text-neutral-alpha-7 min-w-0"
            />
            {searchValue && (
              <Button className="p-1 text-neutral-alpha-7 hover:text-neutral-text transition-colors">
                <RiCloseLine className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>
        </SearchField>
        <ListBox
          aria-label={label ?? 'Options'}
          selectionMode="multiple"
          // react-aria defaults to 'clearSelection', which turns Esc into onChange([]) — a saved wipe for
          // consumers that persist on change. Esc must only close the popover (handled by Popover).
          escapeKeyBehavior="none"
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
          className="p-1.5 outline-none max-h-[300px] overflow-auto"
          renderEmptyState={() =>
            isLoading ? (
              <div className="px-2 py-1.5 text-14 text-neutral-alpha-7 flex items-center gap-2">
                <RiLoader4Line className="h-4 w-4 animate-spin" aria-hidden="true" />
                Loading...
              </div>
            ) : (
              <div className="px-2 py-1.5 text-14 text-neutral-alpha-7">{noOptionsText}</div>
            )
          }
        >
          {filteredOptions.map((option) => (
            <ListBoxItem
              key={getKey(option)}
              id={getKey(option)}
              textValue={getOptionLabel(option)}
              className={cn(
                'relative flex items-center gap-2 px-2.5 py-1.5 rounded-md cursor-pointer outline-none text-14 text-neutral-text',
                'hover:bg-[rgba(10,15,41,0.04)] data-focused:bg-[rgba(10,15,41,0.04)]',
                'data-selected:bg-brand-alpha-1',
                'data-[disabled]:opacity-50 data-[disabled]:pointer-events-none'
              )}
            >
              {({ isSelected, isFocused: isItemFocused }) =>
                renderOption ? (
                  renderOption(option, { isSelected, isFocused: isItemFocused })
                ) : (
                  <>
                    <span className="flex-1 truncate">{getOptionLabel(option)}</span>
                    {isSelected && <RiCheckLine className="h-4 w-4 shrink-0 text-neutral-alpha-8" aria-hidden="true" />}
                  </>
                )
              }
            </ListBoxItem>
          ))}
        </ListBox>
      </Popover>
    </div>
  );
}

AgMultiAutocomplete.displayName = 'AgMultiAutocomplete';
